import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import { fetchMembershipPendingApprovals } from "../../contents/redux/actions/membershipRequestActions";
import {
  Add,
  DeleteRounded,
  ModeEditRounded,
  ViewListRounded,
  MoreVertRounded,
  FilterListRounded,
} from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SearchRequests from "../requestmanagement/SearchRequests";
import DefaultLoader from "../utilities/loaders/DefaultLoader";
import styles from "../utilities/cssmodules/AllRequests.module.css";
import { toast } from "react-toastify";

// Utility function to truncate text
const truncateText = (text, wordLimit = 10) => {
  if (!text) return "N/A";
  const words = text.split(/\s+/);
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};

// Utility function to format dates
const formatDate = (dateString) => {
  if (!dateString || dateString === "0001-01-01T00:00:00") return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const MembershipApprovalsContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { membershipRequests, isLoading, errors } = useSelector((state) => ({
    membershipRequests: state.membershipRequests?.membershipRequests || [],
    isLoading: state.membershipRequests?.isLoading || false,
    errors: state.membershipRequests?.fetchMembershipRequestsError || null,
  }));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMembershipRequest, setSelectedMembershipRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    dispatch(fetchMembershipPendingApprovals());
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (errors) {
      toast.error(errors);
      dispatch({ type: "CLEAR_REQUESTS_ERROR" });
    }
  }, [errors, dispatch]);

  const handleMenuOpen = (event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedMembershipRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMembershipRequest(null);
  };

  const handleViewRequest = (membershipRequest) => {
    if (membershipRequest) {
      navigate("/viewmembershiprequest", { state: membershipRequest });
    }
    handleMenuClose();
  };
  // const handleViewRequest = () => {
  //   if (selectedMembershipRequest) {
  //     navigate("/viewmembershiprequest", { state: selectedMembershipRequest });
  //   }
  //   handleMenuClose();
  // };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredMembershipRequests = useMemo(() => {
    let filtered = membershipRequests || [];

    if (statusFilter) {
      filtered = filtered.filter(
        (request) => (request.statusName || "Submitted") === statusFilter,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter((membershipRequest) =>
        Object.values(membershipRequest).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    return [...filtered].sort(
      (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
    );
  }, [membershipRequests, statusFilter, searchTerm]);

  const paginatedMembershipRequests = useMemo(
    () =>
      filteredMembershipRequests.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredMembershipRequests, page, rowsPerPage],
  );

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          {/* <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go Back"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>Membership Approvals</span>
          </div> */}
        </div>
      </div>
      <div className={styles["wrapper"]}>
        <div className={styles["flex-row-container"]}>
          {/* <button
            className={styles["primary-button"]}
            onClick={() => navigate("/create-membership-request")}
            aria-label="Create Membership Request"
          >
            <Add className={styles["button-icon"]} />
            <span>Create Membership Request</span>
          </button> */}
          <div className={styles["flex-row-container"]}>
            <button className={styles["primary-button1"]} aria-label="Filter Requests">
              <FilterListRounded className={styles["button-icon"]} />
              <section>
                <select
                  className={`${styles["status-filter-dropdown"]} ${styles["secondary-button-dropdown"]}`}
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  aria-label="Select Status Filter"
                >
                  <option value="">All Statuses</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Verified">Verified</option>
                  <option value="Queried">Queried</option>
                </select>
              </section>
            </button>
            <div className={styles["search-container"]}>
              <SearchRequests onSearch={handleSearch} />
            </div>
          </div>
        </div>

        <TableContainer component={Paper} className={styles["table-container"]}>
          {isLoading ? (
            <DefaultLoader />
          ) : (
            <Table>
              <TableHead>
                <TableRow className="TableHead">
                  {/* <TableCell className={styles["head"]}>No</TableCell> */}
                  {/* <TableCell className="head">ID</TableCell> */}
                  <TableCell className="head">Business Name</TableCell>
                  <TableCell className="head">TIN</TableCell>
                  <TableCell className="head">
                    Business Address
                  </TableCell>
                  <TableCell className="head">Email</TableCell>
                  <TableCell className="head">Date Created</TableCell>
                  <TableCell className="head">Cell Number</TableCell>
                  <TableCell className="head">Status</TableCell>
                  <TableCell className="head">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMembershipRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No membership approvals pending your attention
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMembershipRequests.map((membershipRequest, index) => (
                    <TableRow key={membershipRequest.id}>
                      {/* <TableCell>{page * rowsPerPage + index + 1}</TableCell> */}
                      {/* <TableCell>{membershipRequest.id || "N/A"}</TableCell> */}
                      <TableCell>
                        <Tooltip title={membershipRequest.companyName || "N/A"} arrowplacement="top">
                          <span>{truncateText(membershipRequest.companyName)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{membershipRequest.tin || "N/A"}</TableCell>
                      <TableCell>
                        <Tooltip title={membershipRequest.businessAddress || "N/A"} arrowplacement="top">
                          <span>{truncateText(membershipRequest.businessAddress)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{membershipRequest.email || "N/A"}</TableCell>
                      <TableCell>{formatDate(membershipRequest.createdDate)}</TableCell>
                      <TableCell>{membershipRequest.cellNumber || "N/A"}</TableCell>
                      <TableCell>
                        <div
                          className={`${styles["status-cell"]} ${(membershipRequest.statusName || "Submitted") === "Submitted"
                              ? styles["status-submitted"]
                              : (membershipRequest.statusName || "Submitted") ===
                                "Submitted"
                                ? styles["status-pending"]
                                : (membershipRequest.statusName || "Submitted") ===
                                  "Approved"
                                  ? styles["status-approved"]
                                  : (membershipRequest.statusName || "Submitted") ===
                                    "Rejected"
                                    ? styles["status-rejected"]
                                    : (membershipRequest.statusName || "Submitted") ===
                                      "Verified"
                                      ? styles["status-verified"]
                                      : (membershipRequest.statusName || "Submitted") ===
                                        "Queried"
                                        ? styles["status-queried"]
                                        : (membershipRequest.statusName ||
                                          "Submitted") === "Attested"
                                          ? styles["status-attested"]
                                          : (membershipRequest.statusName ||
                                            "Submitted") === "Completed"
                                            ? styles["status-completed"]
                                            : styles["status-default"]
                            }`}
                        >
                          {membershipRequest.statusName || "Submitted"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className={styles["generic-table-action-container"]}
                        >
                          <Tooltip
                            title="Actions"
                            arrow
                            placement="bottom"
                          >
                            <button
                              className="generic-table-action-button"
                              // onClick={(event) => handleMenuOpen(event, membershipRequest)}
                              
                              onClick={() => handleViewRequest(membershipRequest)}
                              aria-label="View details"
                            >
                              {/* <MoreVertRounded /> */}
                              View
                            </button>
                          </Tooltip>
                           {/* <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                          >
                            <MenuItem onClick={handleViewRequest}> */}
                              {/* <ViewListRounded sx={{ mr: 1 }} /> View */}
                            {/* </MenuItem> */}
                            {/* <MenuItem onClick={handleEditRequest}>
                              <ModeEditRounded sx={{ mr: 1 }} /> Edit
                            </MenuItem> */}
                             {/* <MenuItem onClick={handleDeleteRequest}>
                              <DeleteRounded sx={{ mr: 1 }} /> Delete
                            </MenuItem> */}
                          {/* </Menu> */}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={filteredMembershipRequests.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            className={styles["table-pagination"]}
          />
        </TableContainer>
      </div>
    </div>
  );
};

export default MembershipApprovalsContent;
